import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { toast } from 'react-toastify';

const ACCENT = '#137274';
const PREFS_KEY = 'ch_solve_timer_prefs_v1';
const sessionKey = (slug) => `ch_solve_timer_session:${slug || 'unknown'}`;

const DIFFICULTY_TARGETS = {
  easy: 20 * 60 * 1000,
  medium: 30 * 60 * 1000,
  hard: 45 * 60 * 1000,
};

const CUSTOM_PRESETS = [
  { label: '15 min', ms: 15 * 60 * 1000 },
  { label: '20 min', ms: 20 * 60 * 1000 },
  { label: '30 min', ms: 30 * 60 * 1000 },
  { label: '45 min', ms: 45 * 60 * 1000 },
  { label: '60 min', ms: 60 * 60 * 1000 },
];

const defaultPrefs = {
  mode: 'stopwatch', // stopwatch | countdown
  targetMs: 30 * 60 * 1000,
  alarmEnabled: true,
  autoStart: false,
  autoPauseOnAccept: true,
};

const readPrefs = () => {
  try {
    return { ...defaultPrefs, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
  } catch {
    return { ...defaultPrefs };
  }
};

const writePrefs = (prefs) => {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
};

const readSession = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(sessionKey(slug)) || 'null');
  } catch {
    return null;
  }
};

const writeSession = (slug, session) => {
  try {
    localStorage.setItem(sessionKey(slug), JSON.stringify(session));
  } catch {
    /* ignore */
  }
};

const formatDuration = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** Shared AudioContext — browsers block sound until resumed after a user gesture. */
let sharedAudioCtx = null;

const getAudioCtx = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
    sharedAudioCtx = new Ctx();
  }
  return sharedAudioCtx;
};

/** Call from a click/tap so countdown alarm can play later. */
const unlockAlarmAudio = () => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  } catch {
    /* ignore */
  }
};

/**
 * Clear, multi-beep alarm (works after unlockAlarmAudio from Start / settings).
 */
const playAlarmTone = async () => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    // 5 urgent beeps — hard to miss vs a single soft chirp
    const beeps = [
      { t: 0, freq: 880 },
      { t: 0.2, freq: 880 },
      { t: 0.4, freq: 1175 },
      { t: 0.7, freq: 880 },
      { t: 0.9, freq: 1175 },
    ];
    beeps.forEach(({ t, freq }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.22, now + t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.16);
    });
  } catch {
    /* ignore */
  }

  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 300]);
    }
  } catch {
    /* ignore */
  }
};

/**
 * Industry-style solve timer (LeetCode / GfG inspired):
 * stopwatch or countdown, pause/reset, alarm, prefs persisted locally.
 */
const ProblemSolveTimer = ({
  problemSlug,
  difficulty,
  acceptedSignal = 0,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const surface = isDark ? '#161b22' : '#ffffff';

  const [prefs, setPrefs] = useState(readPrefs);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [tick, setTick] = useState(0);
  const [settingsEl, setSettingsEl] = useState(null);
  const [alarmFired, setAlarmFired] = useState(false);
  const hydratedRef = useRef(false);
  const lastAcceptRef = useRef(0);

  // Hydrate per-problem session
  useEffect(() => {
    hydratedRef.current = false;
    const session = readSession(problemSlug);
    const nextPrefs = readPrefs();
    setPrefs(nextPrefs);

    if (session) {
      setElapsedMs(Number(session.elapsedMs) || 0);
      setAlarmFired(Boolean(session.alarmFired));
      if (session.running && session.startedAt) {
        setRunning(true);
        setStartedAt(session.startedAt);
      } else {
        setRunning(false);
        setStartedAt(null);
      }
    } else {
      setElapsedMs(0);
      setRunning(false);
      setStartedAt(null);
      setAlarmFired(false);
      if (nextPrefs.mode === 'countdown' && !nextPrefs.targetMs) {
        const d = String(difficulty || 'medium').toLowerCase();
        setPrefs((p) => ({ ...p, targetMs: DIFFICULTY_TARGETS[d] || DIFFICULTY_TARGETS.medium }));
      }
      if (nextPrefs.autoStart) {
        setRunning(true);
        setStartedAt(Date.now());
      }
    }
    hydratedRef.current = true;
  }, [problemSlug, difficulty]);

  // Persist session
  useEffect(() => {
    if (!hydratedRef.current || !problemSlug) return;
    writeSession(problemSlug, {
      elapsedMs,
      running,
      startedAt,
      alarmFired,
      updatedAt: Date.now(),
    });
  }, [problemSlug, elapsedMs, running, startedAt, alarmFired]);

  // Tick while running (timestamp-based so background tabs stay accurate)
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, [running]);

  const liveElapsed = useMemo(() => {
    if (!running || !startedAt) return elapsedMs;
    return elapsedMs + Math.max(0, Date.now() - startedAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedMs, running, startedAt, tick]);

  const targetMs = prefs.targetMs || DIFFICULTY_TARGETS.medium;
  const overtime = prefs.mode === 'countdown' && liveElapsed >= targetMs;
  const remainingMs = Math.max(0, targetMs - liveElapsed);
  const displayMs =
    prefs.mode === 'countdown' ? (overtime ? liveElapsed - targetMs : remainingMs) : liveElapsed;
  const warning =
    prefs.mode === 'countdown' &&
    !overtime &&
    remainingMs <= 60 * 1000 &&
    liveElapsed > 0;

  // Countdown alarm — fire once when time runs out while the timer is running
  useEffect(() => {
    if (prefs.mode !== 'countdown' || !prefs.alarmEnabled) return;
    if (!overtime || !running || alarmFired) return;

    setAlarmFired(true);
    playAlarmTone();
    toast.warning('Time is up — countdown finished', {
      autoClose: 6000,
      toastId: 'solve-timer-alarm',
    });
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification('Time is up', {
          body: 'Your solve timer has reached the limit.',
          silent: false,
        });
      } catch {
        /* ignore */
      }
    }
    // Freeze at the limit (show 00:00) — user can Reset to go again
    setElapsedMs(targetMs);
    setStartedAt(null);
    setRunning(false);
  }, [prefs.mode, prefs.alarmEnabled, overtime, running, alarmFired, targetMs]);

  // Auto-pause on Accepted submit
  useEffect(() => {
    if (!acceptedSignal || acceptedSignal === lastAcceptRef.current) return;
    lastAcceptRef.current = acceptedSignal;
    if (!prefs.autoPauseOnAccept) return;
    if (!running) return;
    if (startedAt) {
      setElapsedMs((prev) => prev + Math.max(0, Date.now() - startedAt));
    }
    setStartedAt(null);
    setRunning(false);
  }, [acceptedSignal, prefs.autoPauseOnAccept, running, startedAt]);

  const updatePrefs = useCallback((patch) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      writePrefs(next);
      return next;
    });
  }, []);

  const handleStartPause = () => {
    unlockAlarmAudio();
    if (running) {
      if (startedAt) {
        setElapsedMs((prev) => prev + Math.max(0, Date.now() - startedAt));
      }
      setStartedAt(null);
      setRunning(false);
      return;
    }
    setStartedAt(Date.now());
    setRunning(true);
  };

  const handleReset = () => {
    unlockAlarmAudio();
    setRunning(false);
    setStartedAt(null);
    setElapsedMs(0);
    setAlarmFired(false);
  };

  const applyDifficultyTarget = () => {
    const d = String(difficulty || 'medium').toLowerCase();
    updatePrefs({
      mode: 'countdown',
      targetMs: DIFFICULTY_TARGETS[d] || DIFFICULTY_TARGETS.medium,
    });
    handleReset();
  };

  const requestNotifyPermission = async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') return;
    try {
      await Notification.requestPermission();
    } catch {
      /* ignore */
    }
  };

  const timerColor = overtime
    ? '#cf222e'
    : warning
      ? '#d97706'
      : running
        ? ACCENT
        : muted;

  return (
    <>
      <Box
        data-tour="problem-timer"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          pl: 0.75,
          pr: 0.35,
          py: 0.25,
          borderRadius: 1.5,
          border: `1px solid ${border}`,
          bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
        }}
      >
        <Tooltip title={prefs.mode === 'countdown' ? 'Countdown timer' : 'Stopwatch'}>
          <TimerOutlinedIcon sx={{ fontSize: 16, color: timerColor, mr: 0.25 }} />
        </Tooltip>
        <Typography
          component="span"
          sx={{
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontWeight: 800,
            fontSize: '0.85rem',
            letterSpacing: '0.02em',
            color: timerColor,
            minWidth: 52,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {formatDuration(displayMs)}
          {overtime ? '+' : ''}
        </Typography>

        <Tooltip title={running ? 'Pause' : 'Start'}>
          <IconButton size="small" onClick={handleStartPause} sx={{ color: timerColor }}>
            {running ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset timer">
          <IconButton size="small" onClick={handleReset} sx={{ color: muted }}>
            <RestartAltIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Timer settings">
          <IconButton
            size="small"
            onClick={(e) => {
              unlockAlarmAudio();
              setSettingsEl(e.currentTarget);
            }}
            sx={{ color: muted }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(settingsEl)}
        anchorEl={settingsEl}
        onClose={() => setSettingsEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${border}`,
            bgcolor: surface,
          },
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25 }}>
          Solve timer
        </Typography>

        <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block', mb: 0.75 }}>
          Mode
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          fullWidth
          value={prefs.mode}
          onChange={(_, v) => {
            if (!v) return;
            updatePrefs({ mode: v });
            setAlarmFired(false);
          }}
          sx={{
            mb: 1.5,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
            },
            '& .Mui-selected': {
              bgcolor: `${alpha(ACCENT, isDark ? 0.22 : 0.1)} !important`,
              color: `${ACCENT} !important`,
            },
          }}
        >
          <ToggleButton value="stopwatch">Stopwatch</ToggleButton>
          <ToggleButton value="countdown">Countdown</ToggleButton>
        </ToggleButtonGroup>

        {prefs.mode === 'countdown' && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block', mb: 0.75 }}>
              Time limit
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {CUSTOM_PRESETS.map((p) => (
                <Button
                  key={p.ms}
                  size="small"
                  variant={prefs.targetMs === p.ms ? 'contained' : 'outlined'}
                  onClick={() => {
                    updatePrefs({ targetMs: p.ms });
                    setAlarmFired(false);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: 0,
                    px: 1,
                    ...(prefs.targetMs === p.ms
                      ? { bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }
                      : { borderColor: border, color: 'inherit' }),
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </Box>
            <Button
              size="small"
              onClick={applyDifficultyTarget}
              sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT, px: 0 }}
            >
              Use {String(difficulty || 'medium').toLowerCase()} default (
              {formatDuration(
                DIFFICULTY_TARGETS[String(difficulty || 'medium').toLowerCase()] ||
                  DIFFICULTY_TARGETS.medium,
              )}
              )
            </Button>
            <TextField
              select
              size="small"
              fullWidth
              label="Or pick limit"
              value={prefs.targetMs}
              onChange={(e) => {
                updatePrefs({ targetMs: Number(e.target.value) });
                setAlarmFired(false);
              }}
              sx={{ mt: 1.25 }}
            >
              {CUSTOM_PRESETS.map((p) => (
                <MenuItem key={p.ms} value={p.ms}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Divider sx={{ my: 1.25 }} />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={prefs.alarmEnabled}
              onChange={(e) => {
                const on = e.target.checked;
                unlockAlarmAudio();
                updatePrefs({ alarmEnabled: on });
                if (on) {
                  requestNotifyPermission();
                  playAlarmTone();
                  toast.info('Alarm on — you’ll hear this when time runs out', {
                    autoClose: 2500,
                    toastId: 'solve-timer-alarm-test',
                  });
                }
              }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsActiveOutlinedIcon sx={{ fontSize: 16, color: muted }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                Alarm when countdown ends
              </Typography>
            </Box>
          }
          sx={{ mb: 0.5, ml: 0 }}
        />
        {prefs.mode === 'countdown' && prefs.alarmEnabled && (
          <Button
            size="small"
            onClick={() => {
              unlockAlarmAudio();
              playAlarmTone();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: ACCENT,
              px: 0,
              mb: 0.75,
              display: 'block',
            }}
          >
            Test alarm sound
          </Button>
        )}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={prefs.autoStart}
              onChange={(e) => updatePrefs({ autoStart: e.target.checked })}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
              Auto-start on new problems
            </Typography>
          }
          sx={{ mb: 0.5, ml: 0 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={prefs.autoPauseOnAccept}
              onChange={(e) => updatePrefs({ autoPauseOnAccept: e.target.checked })}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
              Auto-pause on Accepted
            </Typography>
          }
          sx={{ mb: 0.5, ml: 0 }}
        />

        <Typography variant="caption" sx={{ color: muted, display: 'block', mt: 1, lineHeight: 1.4 }}>
          Progress is saved for this problem on this device. Reset clears only the current session.
          Start the timer once so the alarm sound is allowed by your browser.
        </Typography>
      </Popover>
    </>
  );
};

export default ProblemSolveTimer;
